import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "@/ui/cn";
import { Heading } from "./heading";

function Card({
  className,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        "border-border bg-card text-card-foreground overflow-hidden rounded-sm text-sm border has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 data-[size=sm]:has-data-[slot=card-footer]:pb-0 *:[img:first-child]:rounded-t-sm *:[img:last-child]:rounded-b-sm group/card flex flex-col",
        className
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "gap-1 border-b rounded-t-sm px-3 pt-3 pb-2 group-data-[size=sm]/card:px-2 group-data-[size=sm]/card:pt-2 group-data-[size=sm]/card:pb-1.5 group/card-header @container/card-header grid auto-rows-min items-start has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]",
        className
      )}
      {...props}
    />
  );
}

function CardTitle({
  className,
  asChild = false,
  level = "h1",
  ...props
}: React.ComponentProps<"h1"> & {
  asChild?: boolean;
  level?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}) {
  const Comp = asChild ? Slot : Heading;
  return (
    <Comp
      level={asChild ? undefined : level}
      data-slot="card-title"
      className={cn("group-data-[size=sm]/card:text-sm", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("p-3 group-data-[size=sm]/card:p-2", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "bg-muted/50 rounded-b-sm border-t p-3 group-data-[size=sm]/card:p-2 flex items-center",
        className
      )}
      {...props}
    />
  );
}

export { Card, CardHeader, CardFooter, CardTitle, CardAction, CardDescription, CardContent };
